<script setup>
import ListingForm from '@/components/Listing/ListingForm.vue'
import { useApi } from '@/composables/useApi'
import { useListingStore } from '@/stores/listing'
import { computed, onMounted, ref, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'

definePage({
  meta: {
    requiresAuth: true,
    roles: ['surgeon_secretary'],
  },
})

const route = useRoute()
const router = useRouter()
const listingStore = useListingStore()

const initialData = ref(null)
const loadError = ref('')
const isLoading = ref(true)
const listingFormRef = ref(null)

const listingId = computed(() => route.params.id)

const toLocalDateTimeInput = value => {
  if (!value) return null
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return null

  const pad = number => String(number).padStart(2, '0')
  const year = date.getFullYear()
  const month = pad(date.getMonth() + 1)
  const day = pad(date.getDate())
  const hours = pad(date.getHours())
  const minutes = pad(date.getMinutes())

  return `${year}-${month}-${day}T${hours}:${minutes}`
}

const mapListingToFormData = listing => {
  const payload = listing?.data ?? listing ?? {}
  const specialistAreas = Array.isArray(payload.specialist_areas) ? payload.specialist_areas : []

  return {
    ...payload,
    surgeon_id: payload.surgeon_id ?? payload.surgeon?.id ?? null,
    hospital_id: payload.hospital_id ?? payload.hospital?.id ?? null,
    specialist_area_ids: Array.isArray(payload.specialist_area_ids)
      ? payload.specialist_area_ids
      : specialistAreas.map(area => area?.id).filter(id => id != null),
    scheduled_at: toLocalDateTimeInput(payload.scheduled_at),
    duration_minutes: payload.metadata?.duration_minutes ?? payload.duration_minutes ?? 120,
    target_anaesthetists: payload.target_anaesthetists ?? [],
    package_line_items: Array.isArray(payload.package_line_items) ? payload.package_line_items : [],
  }
}

const fetchInitialData = async () => {
  isLoading.value = true
  loadError.value = ''

  try {
    const { data, error } = await useApi(`/api/listings/${listingId.value}/edit`)
    if (error.value)
      throw error.value

    initialData.value = mapListingToFormData(data.value)
  }
  catch (err) {
    loadError.value = 'Unable to load the listing.'
    console.error('Failed to load listing', err)
  }
  finally {
    isLoading.value = false
  }
}

onMounted(fetchInitialData)

watch(listingId, () => {
  fetchInitialData()
})

const handleUpdate = async payload => {
  try {
    await listingStore.updateListing(listingId.value, payload)
    router.push({
      name: 'surgeon_secretary-surgeon-secretary-dashboard-page',
      query: { updated: listingId.value },
    })
  }
  catch (err) {
    console.error('Failed to update listing', err)
  }
}

const handleSaveDraft = async payload => {
  try {
    await listingStore.updateListing(listingId.value, payload)
    router.push({
      name: 'surgeon_secretary-surgeon-secretary-dashboard-page',
      query: { draft_saved: listingId.value },
    })
  }
  catch (err) {
    console.error('Failed to save draft listing', err)
  }
}

const handleCancel = () => {
  router.push({ name: 'surgeon_secretary-surgeon-secretary-dashboard-page' })
}

const triggerSubmit = () => {
  listingFormRef.value?.submit?.()
}
</script>

<template>
  <VContainer fluid>
    <!-- Header Row -->
    <VRow class="mb-4">
      <VCol cols="12">
        <div class="d-flex flex-wrap align-center justify-space-between gap-4">
          <div class="d-flex align-center">
            <VIcon
              icon="ri-file-edit-line"
              size="32"
              class="mr-2"
            />
            <h1 class="text-h4">
              Edit Listing
            </h1>
          </div>
          <div class="d-flex gap-2">
            <VBtn
              variant="outlined"
              @click="handleCancel"
            >
              Cancel
            </VBtn>
            <VBtn
              color="primary"
              :loading="isLoading"
              @click="triggerSubmit"
            >
              Save Changes
            </VBtn>
          </div>
        </div>
      </VCol>
    </VRow>

    <!-- Content Row -->
    <VRow>
      <VCol cols="12">
        <VAlert
          v-if="loadError"
          type="error"
          variant="tonal"
          class="mb-6"
        >
          {{ loadError }}
        </VAlert>

        <VProgressLinear
          v-else-if="isLoading"
          indeterminate
          class="mb-6"
        />

        <ListingForm
          v-else
          ref="listingFormRef"
          :initial-data="initialData"
          is-edit
          @submit="handleUpdate"
          @save-draft="handleSaveDraft"
          @cancel="handleCancel"
        />
      </VCol>
    </VRow>
  </VContainer>
</template>
